import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    CarouselModule as CarouselModuleValue,
    ComparisonModule as ComparisonModuleValue,
    DetailsModule as DetailsModuleValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CarouselModule } from "../../blocks/CarouselModule";
import { ComparisonModule } from "../../blocks/ComparisonModule";
import { DetailsModule } from "../../blocks/DetailsModule";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

// The PDP `modules` dispatch map: the 11 shared Toolbox keys plus the three
// PDP-specific module renderers. Exported so the block-screenshot styleguide
// (StyleguideBlockTemplate) drives `carousel` (CarouselModule) through the
// identical production map. `features` (FeaturesBlock) / `reviews_full` and the
// configurator conditionals live on the ProductLinePage body map, which closes
// over page-level SSR side channels the styleguide cannot supply.
export const productDisplayPageBodyComponentsMap = {
    ...toolboxComponentsMap,
    carousel: (block: { value: Immutable<CarouselModuleValue> }) => (
        <CarouselModule value={block.value} />
    ),
    comparison: (block: { value: Immutable<ComparisonModuleValue> }) => (
        <ComparisonModule value={block.value} />
    ),
    details: (block: { value: Immutable<DetailsModuleValue> }) => (
        <DetailsModule value={block.value} />
    ),
} as const;

/**
 * toppers ProductDisplayPage ``modules`` TOP body assembly.
 *
 * FAITHFUL port of the ``cms/product_display_page.html`` body loop, which was a
 * single ``{% for block in page.modules %}`` driven by ``{% if %}/{% else %}``
 * branches whose ``{% else %}`` fell through to ``{% include_block block %}``.
 * The one type the loop split OUT into its own bottom hydration root is
 * ``offer`` -- it renders below this island, in the disjoint
 * ``ProductDisplayPageOfferTemplate``.
 *
 * NO SILENT DROP here: this loop's
 * ``{% else %}`` rendered EVERY non-``offer`` block type via ``include_block``,
 * so this island maps ALL 14 non-offer keys of ``ProductDisplayPageBodyContent``
 * (the 11 Toolbox keys + carousel/comparison/details). We achieve that by
 * SPREADING the full ``toolboxComponentsMap`` (which covers the 11 toolbox
 * keys, including the recursive ``promo_conditional_block`` and the SSR-envelope
 * grids) and layering on the three PDP-specific keys.
 *
 * The ``offer`` block MUST be filtered out of the value array first: the
 * components map has no ``offer`` renderer, and thelabui ``StreamField`` THROWS
 * on an unmapped block type. The ``isNotOffer`` type predicate narrows the
 * value's discriminant union to the 14 non-offer members so the 14-key map
 * type-checks exactly (and the runtime throw enforces that ``offer`` never
 * reaches this island).
 */
type Block = NonNullable<
    Immutable<{
        page: templates.ProductDisplayPageTemplate["page"];
    }>["page"]["modules"]
>["value"][number];

const isNotOffer = (block: Block): block is Exclude<Block, { type: "offer" }> =>
    block.type !== "offer";

export const ProductDisplayPageBody = (
    props: Immutable<{ page: templates.ProductDisplayPageTemplate["page"] }>,
): React.JSX.Element | null => {
    const modules = props.page.modules;
    if (!modules) {
        return null;
    }
    const value = modules.value.filter(isNotOffer);
    return (
        <StreamField
            tagName={React.Fragment}
            value={value}
            components={productDisplayPageBodyComponentsMap}
        />
    );
};
